import { Star } from "@phosphor-icons/react";
import clsx from "clsx";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useFavorite from "../datahooks/useFavorite";

import type React from "react";

interface FavoriteLeadProps {
  leadId: string;
  isFavorite: boolean;
  isLead?: boolean;
}

export default function FavoriteLead({
  leadId,
  isFavorite,
  isLead = true,
}: FavoriteLeadProps) {
  const { addFavorite, removeFavorite } = useFavorite();
  const isTabletOrDesktop = useTwBreakpoint("md");

  if (!isLead) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger>
            <Star size={16} weight="fill" className="text-black-300" />
          </TooltipTrigger>

          <TooltipContent side={isTabletOrDesktop ? "top" : "bottom"}>
            <p>Only leads can be marked as favorites</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isFavorite) {
      removeFavorite({ leadId });
      return;
    }

    addFavorite({ leadId });
  };

  const ariaLabel = isFavorite ? "Remove from favorites" : "Add to favorites";

  return (
    <button
      type="button"
      className="group relative flex"
      aria-label={ariaLabel}
      onClick={handleFavoriteClick}
    >
      <Star
        className={clsx(
          "min-h-3.5 min-w-3.5",
          isFavorite
            ? "text-yellow-500 hover:text-yellow-600"
            : "text-black-300 group-hover/table-row:text-black-700",
        )}
        weight={isFavorite ? "fill" : "regular"}
        size={16}
      />

      <Star
        size={16}
        weight="fill"
        className="absolute text-black-700 left-0 top-0 z-10 opacity-0 group-hover:opacity-100"
      />
    </button>
  );
}
